<template>
  <b-container fluid :key="country + '_' + period">
    <h1>Location Performance</h1>
    <div v-if="loaded">
      <b-container fluid>
        <b-row>
          <b-col><mio-select v-model="country" label="Select country:" all-label="All countries" :options="LocationPerformanceData.filters.countries" /></b-col>
          <b-col><mio-select v-model="period" label="Select period:" all-label="2019 – present" :options="LocationPerformanceData.filters.periods" /></b-col>
        </b-row>
      </b-container>
      <mio-output
        :key="country + '_' + period"
        :method="LocationPerformanceData.method"
        :messages="LocationPerformanceData.messages"
        :tables="LocationPerformanceData.tables"
      />
    </div>
    <h2 v-else>Loading...</h2>
  </b-container>
</template>

<script>
import { mapActions } from 'vuex';
import { set, get, validateRow, slugify } from '@/utils/MioHelpers';
import MioSelect from '@/components/MioSelect';
import MioOutput from '@/components/MioOutput';

export default {
  name: 'MioLocationPerformance',
  components: {
    MioSelect,
    MioOutput,
  },
  data() {
    return {
      country: '*',
      period: '*',
      yearFrom: 2019,
      loaded: false,
    }
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions({
      GET_GROUP_COUNT_2: "mio/GET_GROUP_COUNT_2",
      GET_TEACHER_COUNT: "mio/GET_TEACHER_COUNT",
      GET_SCHOOL_COUNT: "mio/GET_SCHOOL_COUNT",
      GET_MODULE_VISIT_COUNT: "mio/GET_MODULE_VISIT_COUNT",
      GET_MAINTENANCE_VISIT_COUNT: "mio/GET_MAINTENANCE_VISIT_COUNT",
      GET_CERTIFICATES: "mio/GET_CERTIFICATES",
      GET_DATA_CONSENT_YEAR_DISTRICT: "mio/GET_DATA_CONSENT_YEAR_DISTRICT",
      GET_PARENT_CERTIFICATES_DISTRICT: "mio/GET_PARENT_CERTIFICATES_DISTRICT",
      GET_TEACHERS_UNDER_MAINTENANCE: "mio/GET_TEACHERS_UNDER_MAINTENANCE",
    }),
    async getData() {
      try {
        await this.GET_GROUP_COUNT_2();
        await this.GET_TEACHER_COUNT();
        await this.GET_SCHOOL_COUNT();
        await this.GET_MODULE_VISIT_COUNT();
        await this.GET_MAINTENANCE_VISIT_COUNT();
        await this.GET_CERTIFICATES();
        await this.GET_DATA_CONSENT_YEAR_DISTRICT();
        await this.GET_PARENT_CERTIFICATES_DISTRICT();
        await this.GET_TEACHERS_UNDER_MAINTENANCE();

        this.loaded = true;
      } catch (err) {
        console.error(err);
      }
    },
  },
  computed: {
    LocationPerformanceData: {
      get() {
        let input = {
          groups: this.$store.state.mio.groupCount2,
          teachers: this.$store.state.mio.teacherCount,
          schools: this.$store.state.mio.schoolCount,
          moduleVisits: this.$store.state.mio.moduleVisitCount,
          maintenanceVisits: this.$store.state.mio.maintenanceVisitCount,
          certificates: this.$store.state.mio.certificates,
          consents: this.$store.state.mio.dataConsentYearDistrict,
          parentCertificates: this.$store.state.mio.parentCertificatesDistrict,
          teachersUnderMaintenance: this.$store.state.mio.teachersUnderMaintenance,
        }
        let working = {};
        let output = {
          method: "Groups, teachers and schools are taken from their respective tables and ordered by country, region and district. Class visits are filtered from the classroom observations list. Teacher certificates are taken from recipients of the 'Certificate Ceremony' in the training modules table; parent certificates come from their own table. Teachers currently under maintenance visits are filtered from the records of all maintenance visits in the last 6 months. ",
          messages: [],
        };
        let cols = {
          groups: 'groups',
          teachers: 'teachers',
          certificates: 'teacher certificates',
          consents: 'consents',
          parentCertificates: 'parent certificates',
          schools: 'schools',
          moduleVisits: 'module visits',
          maintenanceVisits: 'maintenance visits',
          teachersUnderMaintenance: 'teachers currently under maintenance',
        };
        let countries = {};
        let periods = {},
          currentPeriod = '';

        for (let col in cols) {
          Object.keys(input[col]).forEach(row => {

            // Validate and filter
            let valid, message, country, region, district, count, date, year, month;
            if (col === 'maintenanceVisits' || col === 'moduleVisits') {
              [valid, message, country, region, district, count, date] = validateRow(input[col][row], ['country', 'region', 'district', 'count', 'year_month']);
              year = date.substr(0, 4);
              // The date arrives as "YYYY-MM", so the month starts at index 5. Reading
              // from 4 returned the hyphen and first digit ("-0"), which coerces to -0 and
              // filed every visit ever recorded into January-June.
              month = date.substr(5, 2);
            } else if (col === 'certificates') {
              [valid, message, country, region, district, count, year, month] = validateRow(input[col][row], ['country', 'region', 'district', 'count', 'year', 'month']);
            } else if (col === 'consents') {
              [valid, message, country, region, district, count, year, month] = validateRow(input[col][row], ['country', 'region', 'district', 'count', 'year', 'month']);
            } else if (col === 'parentCertificates') {
              [valid, message, country, region, district, count, year, month] = validateRow(input[col][row], ['country', 'region', 'district', 'parents_uuid', 'year', 'month']);
              count = count.length;
            } else if (col === 'teachersUnderMaintenance') {
              [valid, message, country, region, district, count] = validateRow(input[col][row], ['country', 'region', 'district', 'count']);
            } else {
              [valid, message, country, region, district, count, year, month] = validateRow(input[col][row], ['country', 'region', 'district', `${col}_count`, 'year_created', 'month_created']);
            }

            if (!valid) {
              output.messages.push(`${col} record is missing some data: ${message}`);
              return;
            }

            if (col !== 'teachersUnderMaintenance') {
              if (year < this.yearFrom) {
                return;
              } else {
                if (month <= 6) {
                  currentPeriod = `${year}: January - June`;
                } else {
                  currentPeriod = `${year}: July - December`;
                }
                set(periods, currentPeriod);
              }

              if (this.period != '*' && currentPeriod !== this.period) {
                return;
              }
            }

            // Apply filters
            set(countries, `${country}`, {});
            if (this.country != '*' && country !== this.country) {
              return;
            }

            // Save
            set(working, `data.${country}.rows.${region}.${district}.${col}`, count, 'add');
          });
        }

        // Split into outputs by country
        output.tables = Object.keys(working.data).sort().map(country => ({
          title: country,
          cols: Object.values(cols),
          rows: Object.keys(working.data[country].rows).sort().map(region => ({
            title: region,
            subRows: Object.keys(working.data[country].rows[region]).sort().map(district => ({
              title: district,
              data: Object.keys(cols).map(col => working.data[country].rows[region][district][col]),
            })),
          })),
        }));

        // Add country filters
        output.filters = {
          countries: Object.keys(countries).sort(),
          periods: Object.keys(periods).sort(),
        };

        console.log(working);
        // console.log(output);
        return output;
      }
    },
  },
};
</script>
